import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";

const BASE_URL = "https://pbhrm.com/api/";
const IMAGE_BASE_URL = "https://pbhrm.com/uploads/";

const ParcelList = () => {
  const [parcels, setParcels] = useState([]);
  const [filteredParcels, setFilteredParcels] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedParcel, setSelectedParcel] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${BASE_URL}parcel/`)
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 1) {
          setParcels(data.data);
          setFilteredParcels(data.data);
        } else {
          alert("Failed to fetch parcels");
        }
      })
      .catch((err) => console.error("Error fetching parcels:", err));
  }, []);

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    const filtered = parcels.filter((p) =>
      p.name.toLowerCase().includes(term.toLowerCase())
    );
    setFilteredParcels(filtered);
  };

  const handleEdit = (id) => navigate(`/EditParcel/${id}`);

  const handleAdd = () => navigate("/AddParcel");

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This parcel will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(`${BASE_URL}parcel/${id}`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status === 1) {
              const updated = parcels.filter((p) => p.id !== id);
              setParcels(updated);
              setFilteredParcels(updated);
              Swal.fire("Deleted!", "Parcel has been deleted.", "success");
            } else {
              Swal.fire("Failed!", "Failed to delete parcel.", "error");
            }
          })
          .catch((err) => {
            console.error("Error deleting parcel:", err);
            Swal.fire("Error!", "Something went wrong.", "error");
          });
      }
    });
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Parcel List", 14, 10);
    const tableData = filteredParcels.map((p) => [
      p.id,
      p.name || "N/A",
      p.image ? `${IMAGE_BASE_URL}${p.image}` : "No Image",
      p.amount || "N/A",
      p.status || "N/A",
      p.created_at || "N/A",
    ]);
    doc.autoTable({
      head: [["ID", "Name", "Image URL", "Amount", "Status", "Created At"]],
      body: tableData,
    });
    doc.save("parcels-list.pdf");
  };

  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredParcels.map((p) => ({
        ID: p.id,
        Name: p.name,
        Image: p.image ? `${IMAGE_BASE_URL}${p.image}` : "No Image",
        Amount: p.amount,
        Status: p.status,
        CreatedAt: p.created_at,
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Parcel List");
    XLSX.writeFile(workbook, "parcels-list.xlsx");
  };

  const openModal = (parcel) => {
    setSelectedParcel(parcel);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedParcel(null);
  };

  const handleStatusChange = async (status) => {
    if (selectedParcel) {
      try {
        console.log(
          `Updating parcel ${selectedParcel.id} status to: ${status}`
        );

        // Try PUT method first
        let response = await fetch(
          `${BASE_URL}parcel/${selectedParcel.id}/status`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ status }),
          }
        );

        // If PUT fails, try POST
        if (!response.ok) {
          console.log("PUT failed, trying POST...");
          response = await fetch(
            `${BASE_URL}parcel/${selectedParcel.id}/status`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ status }),
            }
          );
        }

        // If POST fails, try PATCH
        if (!response.ok) {
          console.log("POST failed, trying PATCH...");
          response = await fetch(
            `${BASE_URL}parcel/${selectedParcel.id}/status`,
            {
              method: "PATCH",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ status }),
            }
          );
        }

        // Check if any request worked
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log("API Response:", data);

        if (data.status === 1) {
          const updated = parcels.map((p) =>
            p.id === selectedParcel.id ? { ...p, status: data.data.status } : p
          );
          setParcels(updated);
          setFilteredParcels(updated);
          closeModal();
          Swal.fire("Success!", "Status updated successfully.", "success");
        } else {
          Swal.fire(
            "Failed!",
            data.message || "Failed to update status.",
            "error"
          );
        }
      } catch (err) {
        console.error("Error updating status:", err);

        // Alternative: Try updating without API call (frontend only)
        const confirmUpdate = await Swal.fire({
          title: "API Error",
          text: "Server not responding. Update status locally?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, update locally",
          cancelButtonText: "Cancel",
        });

        if (confirmUpdate.isConfirmed) {
          const updated = parcels.map((p) =>
            p.id === selectedParcel.id ? { ...p, status: status } : p
          );
          setParcels(updated);
          setFilteredParcels(updated);
          closeModal();
          Swal.fire("Updated!", "Status updated locally.", "info");
        }
      }
    }
  };

  return (
    <div id="main">
      <header className="mb-3">
        <a href="#" className="burger-btn d-block d-xl-none">
          <i className="bi bi-justify fs-3"></i>
        </a>
      </header>

      <div className="page-heading">
        <div className="page-title">
          <div className="row">
            <div className="col-12 col-md-6 order-md-1 order-last">
              <h3>Parcel List</h3>
            </div>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="row" id="basic-table">
          <div className="col-12 col-md-12">
            <div className="card">
              <div className="card-header">
                <div className="row">
                  <div className="col-md-6">
                    <label className="form-label">Search by Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search parcel name"
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </div>
                  <div className="col-md-6 text-end">
                    {/* <button
                      className="btn btn-primary me-2"
                      onClick={handleAdd}
                    >
                      Add Parcel
                    </button> */}
                    <button
                      className="btn btn-secondary me-2"
                      onClick={handleExportPDF}
                    >
                      Export to PDF
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={handleExportExcel}
                    >
                      Export to Excel
                    </button>
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-lg">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>NAME</th>
                        <th>IMAGE</th>
                        <th>AMOUNT</th>
                        <th>STATUS</th>
                        <th>CREATED AT</th>
                        <th className="text-end">ACTION</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredParcels.map((parcel) => (
                        <tr key={parcel.id}>
                          <td>{parcel.id}</td>
                          <td>{parcel.name || "N/A"}</td>
                          <td>
                            {parcel.image ? (
                              <img
                                src={`${IMAGE_BASE_URL}vp_images/${parcel.image}`}
                                alt={parcel.name}
                                style={{ width: "100px", height: "auto" }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              "No Image"
                            )}
                          </td>
                          <td>{parcel.amount || "N/A"}</td>
                          <td>
                            <span
                              className={`badge ${
                                parcel.status === "completed"
                                  ? "bg-success"
                                  : parcel.status === "pending"
                                  ? "bg-warning"
                                  : parcel.status === "canceled"
                                  ? "bg-danger"
                                  : "bg-secondary"
                              }`}
                            >
                              {parcel.status || "N/A"}
                            </span>
                          </td>
                          <td>{parcel.created_at || "N/A"}</td>
                          <td className="text-end">
                            {/* <button
                              className="btn btn-success me-2"
                              onClick={() => handleEdit(parcel.id)}
                            >
                              Edit
                            </button> */}
                            <button
                              className="btn btn-danger me-2"
                              onClick={() => handleDelete(parcel.id)}
                            >
                              Delete
                            </button>
                            <button
                              className="btn btn-info"
                              onClick={() => openModal(parcel)}
                            >
                              Change Status
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {isModalOpen && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Change Status for {selectedParcel?.name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-3">Select new status:</p>
                <div className="d-flex justify-content-between">
                  <button
                    className="btn btn-warning"
                    onClick={() => handleStatusChange("pending")}
                  >
                    Pending
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={() => handleStatusChange("completed")}
                  >
                    Completed
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleStatusChange("canceled")}
                  >
                    Canceled
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParcelList;
